import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { listContracts } from 'services/contractService'
import { RAG } from 'features/dashboard/charts/scoreColour'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import SearchableSelect from 'components/SearchableSelect'
import ContractModal from 'features/contracts/ContractModal'

const EXPIRY = {
  expired:    { label: 'Expired',    rag: RAG.critical },
  expiring:   { label: 'Expiring',   rag: RAG.high },
  active:     { label: 'Active',     rag: RAG.low },
  terminated: { label: 'Terminated', rag: RAG.none },
  draft:      { label: 'Draft',      rag: RAG.none },
}
const CRIT = { critical: RAG.critical, high: RAG.high, medium: RAG.medium, low: RAG.low }
const CRIT_ORDER = { low: 1, medium: 2, high: 3, critical: 4 }
const EXPIRY_ORDER = { active: 1, expiring: 2, expired: 3, terminated: 4, draft: 5 }
const TYPE_OPTS = [
  ['security', 'Security'], ['cleaning', 'Cleaning'], ['m_and_e', 'M&E'],
  ['life_safety', 'Life Safety'], ['grounds_maintenance', 'Grounds Maintenance'],
  ['pest_control', 'Pest Control'], ['window_cleaning', 'Window Cleaning'],
]

const ContractsTab = () => {
  const [list, setList] = useState([])
  const [meta, setMeta] = useState({ currentPage: 0, lastPage: 1, total: 0 })
  const [portfolioMap, setPortfolioMap] = useState({})
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState('building')
  const [buildingMode, setBuildingMode] = useState('all')
  const [modal, setModal] = useState(null)
  const [canCreate, setCanCreate] = useState(false)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [critFilter, setCritFilter] = useState('all')
  const [expiryFilter, setExpiryFilter] = useState('all')
  const [insuranceFilter, setInsuranceFilter] = useState('all')
  const [sort, setSort] = useState({ key: null, dir: 'asc' })

  const sortBy = key => setSort(s => s.key === key
    ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: 'asc' })

  const fetch = useCallback(async page => {
    setLoading(true)
    try {
      const { data } = await listContracts({ page })
      const incoming = data?.data || []
      const m = data?.meta || {}
      setMeta(m)
      setCanCreate(!!data?.reference?.canCreate)
      setPortfolioMap(prev => ({ ...prev, ...(data?.reference?.buildingPortfolio || {}) }))
      setList(prev => (m.currentPage ?? 1) > 1
        ? [...prev, ...incoming.filter(i => !prev.some(x => x.id === i.id))]
        : incoming)
    } catch (e) {
      toast.error('Could not load contracts.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetch(1) }, [fetch])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let rows = list.filter(c => {
      if (typeFilter !== 'all' && c.contractType !== typeFilter) return false
      if (critFilter !== 'all' && c.criticality !== critFilter) return false
      if (expiryFilter !== 'all' && c.expiryState !== expiryFilter) return false
      if (insuranceFilter === 'in_place' && !c.insuranceInPlace) return false
      if (insuranceFilter === 'missing' && c.insuranceInPlace) return false
      if (!q) return true
      const hay = [
        c.contractor?.name, c.contractor?.company, c.title, c.reference, c.supplierReference,
        c.contractTypeLabel, ...(c.buildings || []).map(b => b.name),
      ].filter(Boolean).join(' ').toLowerCase()
      return hay.includes(q)
    })
    if (sort.key) {
      const dir = sort.dir === 'asc' ? 1 : -1
      const val = c => {
        switch (sort.key) {
          case 'contractor': return (c.contractor?.name || '').toLowerCase()
          case 'type': return (c.contractTypeLabel || '').toLowerCase()
          case 'buildings': return (c.buildings || []).length
          case 'criticality': return CRIT_ORDER[c.criticality] || 0
          case 'value': return c.valueRestricted ? -1 : Number(c.value || 0)
          case 'expiry': return EXPIRY_ORDER[c.expiryState] || 9
          case 'insurance': return c.insuranceInPlace ? 1 : 0
          default: return 0
        }
      }
      rows = [...rows].sort((a, b) => {
        const av = val(a), bv = val(b)
        if (av < bv) return -1 * dir
        if (av > bv) return 1 * dir
        return 0
      })
    }
    return rows
  }, [list, search, typeFilter, critFilter, expiryFilter, insuranceFilter, sort])

  const filtersActive = search.trim() !== '' || typeFilter !== 'all' || critFilter !== 'all'
    || expiryFilter !== 'all' || insuranceFilter !== 'all'
  const clearFilters = () => {
    setSearch(''); setTypeFilter('all'); setCritFilter('all'); setExpiryFilter('all'); setInsuranceFilter('all')
  }

  const groups = useMemo(() => {
    if (view === 'type') {
      const by = {}
      filtered.forEach(c => { (by[c.contractTypeLabel] ??= []).push(c) })
      return Object.entries(by).sort((a, b) => a[0].localeCompare(b[0])).map(([title, rows]) => ({ title, rows }))
    }
    if (view === 'building' && buildingMode === 'portfolio') {
      const by = {}
      filtered.forEach(c => {
        const names = [...new Set((c.buildings || []).map(b => portfolioMap[b.id]?.name || 'Unassigned'))]
        ;(names.length ? names : ['Unassigned']).forEach(n => { (by[n] ??= []).push(c) })
      })
      return Object.entries(by)
        .sort((a, b) => (a[0] === 'Unassigned' ? 1 : b[0] === 'Unassigned' ? -1 : a[0].localeCompare(b[0])))
        .map(([title, rows]) => ({ title, rows }))
    }
    return [{ title: null, rows: filtered }]
  }, [filtered, view, buildingMode, portfolioMap])

  const totalAnnual = useMemo(() => list.reduce((sum, c) =>
    (!c.valueRestricted && c.valueBasis === 'annual' && c.value ? sum + Number(c.value) : sum), 0), [list])
  const anyRestricted = useMemo(() => list.some(c => c.valueRestricted), [list])

  return (
    <Wrap>
      <Controls>
        <ViewGroup>
          <ViewBtn $active={view === 'building'} onClick={() => setView('building')}>View by Building</ViewBtn>
          <ViewBtn $active={view === 'type'} onClick={() => setView('type')}>View by Contract Type</ViewBtn>
        </ViewGroup>
        {view === 'building' && (
          <ViewGroup>
            <ViewBtn $sub $active={buildingMode === 'all'} onClick={() => setBuildingMode('all')}>All Building Passports</ViewBtn>
            <ViewBtn $sub $active={buildingMode === 'portfolio'} onClick={() => setBuildingMode('portfolio')}>Portfolio View</ViewBtn>
          </ViewGroup>
        )}
        <Spacer />
        {canCreate && <AddBtn type='button' onClick={() => setModal({})}>+ New contract</AddBtn>}
      </Controls>

      <FilterBar>
        <SearchInput placeholder='Search contractor, title, reference, building…' value={search} onChange={e => setSearch(e.target.value)} />
        <FilterWrap>
          <SearchableSelect
            value={typeFilter}
            onChange={v => setTypeFilter(v)}
            options={[{ value: 'all', label: 'All types' }, ...TYPE_OPTS.map(([v, l]) => ({ value: v, label: l }))]}
            ariaLabel='Filter by type'
          />
        </FilterWrap>
        <FilterWrap>
          <SearchableSelect
            value={critFilter}
            onChange={v => setCritFilter(v)}
            options={[
              { value: 'all', label: 'All criticalities' },
              { value: 'critical', label: 'Critical', color: CRIT.critical.fg },
              { value: 'high', label: 'High', color: CRIT.high.fg },
              { value: 'medium', label: 'Medium', color: CRIT.medium.fg },
              { value: 'low', label: 'Low', color: CRIT.low.fg },
            ]}
            ariaLabel='Filter by criticality'
          />
        </FilterWrap>
        <FilterWrap>
          <SearchableSelect
            value={expiryFilter}
            onChange={v => setExpiryFilter(v)}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'active', label: 'Active', color: EXPIRY.active.rag.fg },
              { value: 'expiring', label: 'Expiring', color: EXPIRY.expiring.rag.fg },
              { value: 'expired', label: 'Expired', color: EXPIRY.expired.rag.fg },
              { value: 'terminated', label: 'Terminated', color: EXPIRY.terminated.rag.fg },
              { value: 'draft', label: 'Draft', color: EXPIRY.draft.rag.fg },
            ]}
            ariaLabel='Filter by status'
          />
        </FilterWrap>
        <FilterWrap>
          <SearchableSelect
            value={insuranceFilter}
            onChange={v => setInsuranceFilter(v)}
            options={[
              { value: 'all', label: 'All insurance' },
              { value: 'in_place', label: 'Insurance in place', color: RAG.low.fg },
              { value: 'missing', label: 'No / expired insurance', color: RAG.critical.fg },
            ]}
            ariaLabel='Filter by insurance'
          />
        </FilterWrap>
        {filtersActive && <ClearBtn type='button' onClick={clearFilters}>Clear filters</ClearBtn>}
      </FilterBar>

      <Count>Showing {filtered.length} of {meta.total ?? list.length} contract{(meta.total ?? list.length) === 1 ? '' : 's'}</Count>

      {!loading && filtered.length === 0 ? (
        <Empty>{list.length === 0 ? (canCreate ? 'No contracts yet. Add one with “New contract”.' : 'No contracts to show.') : 'No contracts match your search or filters.'}</Empty>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <SortTh $active={sort.key === 'contractor'} onClick={() => sortBy('contractor')}>Contractor{sort.key === 'contractor' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</SortTh>
                <SortTh $active={sort.key === 'type'} onClick={() => sortBy('type')}>Type{sort.key === 'type' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</SortTh>
                <SortTh $active={sort.key === 'buildings'} onClick={() => sortBy('buildings')}>Buildings{sort.key === 'buildings' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</SortTh>
                <SortTh $active={sort.key === 'criticality'} onClick={() => sortBy('criticality')}>Criticality{sort.key === 'criticality' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</SortTh>
                <SortTh $active={sort.key === 'value'} onClick={() => sortBy('value')}>Value{sort.key === 'value' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</SortTh>
                <SortTh $active={sort.key === 'expiry'} onClick={() => sortBy('expiry')}>Expiry{sort.key === 'expiry' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</SortTh>
                <SortTh $active={sort.key === 'insurance'} onClick={() => sortBy('insurance')}>Insurance{sort.key === 'insurance' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</SortTh>
                <th />
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <React.Fragment key={g.title || 'all'}>
                  {g.title && <tr><GroupCell colSpan={8}>{g.title} · {g.rows.length}</GroupCell></tr>}
                  {g.rows.map(c => {
                    const exp = EXPIRY[c.expiryState] || EXPIRY.active
                    const crit = CRIT[c.criticality] || RAG.medium
                    return (
                      <Row key={`${g.title || 'all'}-${c.id}`} $readonly={c.valueRestricted} onClick={c.valueRestricted ? undefined : () => setModal(c)}>
                        <td><Primary>{c.contractor?.name || '—'}</Primary>{c.title && <Secondary>{c.title}</Secondary>}</td>
                        <td>{c.contractTypeLabel}</td>
                        <td title={(c.buildings || []).map(b => [b.name, b.uprn ? `UPRN ${b.uprn}` : null, [b.street, b.city, b.postalCode].filter(Boolean).join(', ')].filter(Boolean).join(' — ')).join('\n')}>
                          {(c.buildings || []).map(b => b.name).join(', ') || '—'}
                        </td>
                        <td><Tag $rag={crit}>{c.criticality ? c.criticality.charAt(0).toUpperCase() + c.criticality.slice(1) : '—'}</Tag></td>
                        <td>{c.valueRestricted ? <Restricted title='Visible to owners only'>Restricted</Restricted> : (c.valueFormatted || '—')}</td>
                        <td><Tag $rag={exp.rag}>{exp.label}</Tag></td>
                        <td>{c.insuranceInPlace ? <Ok>In place</Ok> : <Bad>None / expired</Bad>}</td>
                        <td>{!c.valueRestricted && <Edit>Edit</Edit>}</td>
                      </Row>
                    )
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
          <InfiniteScrollSentinel
            hasMore={(meta.currentPage ?? 1) < (meta.lastPage ?? 1)}
            loading={loading}
            onLoadMore={() => fetch((meta.currentPage ?? 1) + 1)}
          />
        </TableWrap>
      )}

      {totalAnnual > 0 && (
        <SpendFooter>
          Total annual spend (visible to you): <strong>£{totalAnnual.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          {anyRestricted && <Muted> · some values restricted</Muted>}
        </SpendFooter>
      )}

      {modal && (
        <ContractModal
          contract={modal.id ? modal : null}
          onClose={() => setModal(null)}
          onSaved={() => fetch(1)}
        />
      )}
    </Wrap>
  )
}

export default ContractsTab

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px;`
const Controls = styled.div`display: flex; align-items: center; gap: 10px; flex-wrap: wrap;`
const ViewGroup = styled.div`display: inline-flex; gap: 4px; padding: 3px; background: #f3f4f6; border-radius: 999px;`
const ViewBtn = styled.button`
  font: inherit; border: none; border-radius: 999px; cursor: pointer; padding: ${({ $sub }) => ($sub ? '5px 12px' : '6px 14px')};
  font-size: 12px; font-weight: 700;
  background: ${({ $active }) => ($active ? '#0f172a' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#475569')};
`
const Spacer = styled.div`flex: 1;`
const AddBtn = styled.button`font: inherit; border: none; background: #D24724; color: #fff; border-radius: 999px; padding: 8px 16px; font-size: 13px; font-weight: 700; cursor: pointer;`
const Count = styled.span`font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #8A8F9C;`
const TableWrap = styled.div`
  background: #fff; border: 1px solid rgba(12,18,41,0.06); border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04); overflow: hidden;

  /* The table cannot shrink to a phone; let it scroll inside its own card
     instead of being clipped with no way to reach the last columns. */
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`
const Table = styled.table`
  width: 100%; border-collapse: collapse; text-align: left;

  @media (max-width: 768px) {
    min-width: 640px;
    th { padding: 10px 12px; }
    td { padding: 10px 12px; font-size: 12.5px; }
  }
  thead { background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
  th { padding: 13px 16px; font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #6b7280; }
  td { padding: 13px 16px; font-size: 13px; color: #111827; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
`
const SortTh = styled.th`
  cursor: pointer; user-select: none; white-space: nowrap;
  color: ${({ $active }) => ($active ? '#110c2f' : '#6b7280')} !important;
  &:hover { color: #110c2f !important; }
`
const FilterBar = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`
const SearchInput = styled.input`
  flex: 1; min-width: 220px; padding: 8px 12px; font: inherit; font-size: 13px;
  box-sizing: border-box;
  @media (max-width: 768px) { flex: 1 1 100%; min-width: 0; }
  border: 1px solid #e5e7eb; border-radius: 999px; color: #111827; background: #fff;
  &::placeholder { color: #9ca3af; }
  &:focus { outline: none; border-color: #110c2f; }
`
const FilterWrap = styled.div`
  width: 180px; max-width: 100%; min-width: 0;
`
const ClearBtn = styled.button`
  font: inherit; border: 1px solid #e5e7eb; background: #fff; color: #6b7280;
  border-radius: 999px; padding: 8px 13px; font-size: 13px; font-weight: 700; cursor: pointer;
  &:hover { background: #f9fafb; color: #110c2f; border-color: #d1d5db; }
`
const Row = styled.tr`cursor: ${({ $readonly }) => ($readonly ? 'default' : 'pointer')}; &:hover { background: #f9fafb; }`
const GroupCell = styled.td`background: #fbfbfc; font-size: 11px !important; font-weight: 800 !important; letter-spacing: 0.06em; text-transform: uppercase; color: #6b7280 !important; padding: 8px 16px !important;`
const Primary = styled.div`font-weight: 600; color: #110c2f;`
const Secondary = styled.div`font-size: 12px; color: #6b7280; margin-top: 2px;`
const Tag = styled.span`display: inline-flex; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; text-transform: capitalize; color: ${({ $rag }) => $rag.fg}; background: ${({ $rag }) => $rag.bg}; border: 1px solid ${({ $rag }) => `${$rag.fg}33`};`
const Restricted = styled.span`font-size: 12px; font-style: italic; color: #8A8F9C; cursor: help;`
const Ok = styled.span`font-size: 12px; font-weight: 600; color: #15803d;`
const Bad = styled.span`font-size: 12px; font-weight: 600; color: #b00020;`
const Edit = styled.span`font-size: 12px; font-weight: 700; color: #2aa6b3;`
const Empty = styled.div`background: #fff; border: 1px dashed rgba(12,18,41,0.1); border-radius: 12px; padding: 40px 24px; text-align: center; font-size: 13px; color: #6b7280;`
const SpendFooter = styled.div`font-size: 13px; color: #4A4F5E; padding: 4px 2px;`
const Muted = styled.span`color: #8A8F9C;`
